import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { CalendarDays, MapPin, ArrowRight, Search, Pin, ChevronLeft, ChevronRight, Flame, Mic2 } from "lucide-react";
import { CATEGORY_META, catMeta } from "../lib/categories";
import { slugify } from "../lib/slugify";
import { useCityGeo } from "../lib/useCityGeo";

const dayLabel = (iso) => {
  const d = new Date(iso.length <= 10 ? iso + "T00:00" : iso);
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
};
const timeLabel = (iso) => {
  if (!iso || iso.length <= 10) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};
const monthKey = (iso) => (iso || "").slice(0, 7); // YYYY-MM
const monthLabel = (key) => {
  if (!key) return "";
  const d = new Date(key + "-01T00:00");
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
};

export default function WhatsOn() {
  const [events, setEvents] = useState(null);
  const [query, setQuery] = useState("");
  const [month, setMonth] = useState("all");
  const [category, setCategory] = useState("all");
  const carouselRef = useRef(null);

  useEffect(() => {
    api.get("/events/upcoming").then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  }, []);

  const months = useMemo(() => {
    if (!events) return [];
    return [...new Set(events.map((e) => monthKey(e.event_date)).filter(Boolean))].sort();
  }, [events]);

  const categories = useMemo(() => {
    if (!events) return [];
    return [...new Set(events.map((e) => e.category).filter(Boolean))].sort();
  }, [events]);

  const cityNames = useMemo(() => {
    if (!events) return [];
    return [...new Set(events.map((e) => e.city).filter(Boolean))].sort();
  }, [events]);
  const { city, setCity, nearName } = useCityGeo(cityNames);
  const [venue, setVenue] = useState("all");
  const [artist, setArtist] = useState("all");
  const artists = useMemo(() => {
    if (!events) return [];
    return [...new Map(events.flatMap((e) => e.acts || []).map((a) => [a.slug, a])).values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [events]);
  const venueNames = useMemo(() => {
    if (!events) return [];
    const scope = city === "all" ? events : events.filter((e) => (e.city || "") === city);
    return [...new Set(scope.map((e) => e.venue || e.venue_name).filter(Boolean))].sort();
  }, [events, city]);
  useEffect(() => { setVenue("all"); }, [city]);

  const filtered = useMemo(() => {
    if (!events) return [];
    const q = query.trim().toLowerCase();
    return events.filter((e) => {
      if (month !== "all" && monthKey(e.event_date) !== month) return false;
      if (category !== "all" && (e.category || "") !== category) return false;
      if (city !== "all" && (e.city || "") !== city) return false;
      if (venue !== "all" && (e.venue || e.venue_name || "") !== venue) return false;
      if (artist !== "all" && !(e.acts || []).some((a) => a.slug === artist)) return false;
      if (q && !(`${e.title} ${e.business_name}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [events, query, month, category, city, venue, artist]);

  const scrollCarousel = (dir) => {
    const el = carouselRef.current;
    if (!el) return;
    const amount = 300;
    if (dir > 0 && el.scrollLeft + el.clientWidth >= el.scrollWidth - 8) {
      el.scrollTo({ left: 0, behavior: "smooth" });
    } else if (dir < 0 && el.scrollLeft <= 8) {
      el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    } else {
      el.scrollBy({ left: dir * amount, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return undefined;
    let paused = false;
    const onEnter = () => { paused = true; };
    const onLeave = () => { paused = false; };
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    const id = setInterval(() => { if (!paused) scrollCarousel(1); }, 4000);
    return () => { clearInterval(id); el.removeEventListener("mouseenter", onEnter); el.removeEventListener("mouseleave", onLeave); };
  }, [events, query, month, category]);

  if (!events || events.length === 0) return null;

  const featured = filtered.filter((e) => e.pinned).slice(0, 6);
  const trending = [...events].filter((e) => (e.sold || 0) > 0).sort((a, b) => b.sold - a.sold).slice(0, 6);
  const groups = {};
  filtered.forEach((e) => {
    const key = (e.event_date || "").slice(0, 10) || "tbc";
    (groups[key] = groups[key] || []).push(e);
  });
  const dates = Object.keys(groups).sort();

  return (
    <section id="whats-on" className="py-20 bg-[#0d1626]" data-testid="whats-on-section">
      <div className="max-w-5xl mx-auto px-6">
        <div className="mb-8">
          <p className="text-[#2ec9c9] font-semibold tracking-wide uppercase text-sm">What's on</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mt-2">Upcoming events</h2>
          <p className="text-[#8a97a8] mt-2">Discover events happening soon from organisers on TicketsPro.</p>
        </div>

        {trending.length > 0 && (
          <div className="mb-10" data-testid="trending-row">
            <p className="text-xs uppercase tracking-wide text-[#f4b740] font-semibold mb-3 flex items-center gap-1.5"><Flame size={14} /> Popular this week</p>
            <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollbarWidth: "none" }}>
              {trending.map((e) => (
                <a key={e.id} href={`/store/${e.business_slug}/events/${e.slug}`} data-testid={`trending-card-${e.id}`}
                  className="group w-60 shrink-0 rounded-2xl bg-white/[0.04] border border-white/10 p-3 hover:bg-white/[0.08] hover:border-[#f4b740]/40 transition-colors">
                  <div className="h-28 rounded-xl bg-white/10 overflow-hidden mb-3 relative">
                    {e.image_url && <img src={e.image_url} alt={e.title} className="w-full h-full object-cover" />}
                    <span className="absolute top-2 left-2 text-[10px] font-bold bg-[#f4b740] text-[#0d1626] px-2 py-0.5 rounded-full flex items-center gap-1"><Flame size={10} /> {e.sold} sold</span>
                  </div>
                  <h3 className="text-white font-semibold truncate group-hover:text-[#f4b740] transition-colors">{e.title}</h3>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-[#8a97a8] truncate">{e.business_name}</p>
                    <p className="text-white font-bold text-sm">£{e.from_price.toFixed(2)}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {featured.length > 0 && (
          <div className="mb-10" data-testid="featured-carousel">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-wide text-[#2ec9c9] font-semibold flex items-center gap-1.5"><Pin size={13} /> Featured</p>
              {featured.length > 1 && (
                <div className="flex gap-2">
                  <button onClick={() => scrollCarousel(-1)} data-testid="carousel-prev" aria-label="Previous"
                    className="w-8 h-8 rounded-full border border-white/15 text-white/80 hover:bg-white/10 hover:text-white flex items-center justify-center transition-colors"><ChevronLeft size={16} /></button>
                  <button onClick={() => scrollCarousel(1)} data-testid="carousel-next" aria-label="Next"
                    className="w-8 h-8 rounded-full border border-white/15 text-white/80 hover:bg-white/10 hover:text-white flex items-center justify-center transition-colors"><ChevronRight size={16} /></button>
                </div>
              )}
            </div>
            <div ref={carouselRef} className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x scroll-smooth" style={{ scrollbarWidth: "none" }}>
              {featured.map((e) => {
                const cm = catMeta(e.category); const CIcon = cm.icon;
                return (
                <a key={e.id} href={`/store/${e.business_slug}/events/${e.slug}`} data-testid={`featured-card-${e.id}`}
                  className="group relative w-72 shrink-0 snap-start rounded-2xl overflow-hidden border border-[#2ec9c9]/30 bg-white/[0.04] hover:border-[#2ec9c9] transition-colors">
                  <div className="h-36 bg-white/10 overflow-hidden">
                    {e.image_url && <img src={e.image_url} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                    <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wide bg-[#2ec9c9] text-[#0d1626] font-bold px-2 py-1 rounded-full">Featured</span>
                    {e.category && <span className="absolute top-3 right-3 text-[10px] font-semibold px-2 py-1 rounded-full flex items-center gap-1" style={{ background: cm.color + "26", color: cm.color }}><CIcon size={11} /> {e.category}</span>}
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-[#2ec9c9]">{e.event_date ? dayLabel(e.event_date) : "Date TBC"}{timeLabel(e.event_date) ? ` · ${timeLabel(e.event_date)}` : ""}</p>
                    <h3 className="text-white font-semibold truncate mt-1 group-hover:text-[#2ec9c9] transition-colors">{e.title}</h3>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-[#8a97a8] truncate">{e.business_name}</p>
                      <p className="text-white font-bold text-sm">£{e.from_price.toFixed(2)}</p>
                    </div>
                  </div>
                </a>
              );})}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8a97a8]" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="whatson-search"
              placeholder="Search events or organisers…"
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white placeholder:text-[#8a97a8] outline-none focus:border-[#2ec9c9] transition-colors" />
          </div>
          <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="whatson-month"
            className="bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-white outline-none focus:border-[#2ec9c9] transition-colors">
            <option value="all" className="text-[#16243b]">All months</option>
            {months.map((m) => <option key={m} value={m} className="text-[#16243b]">{monthLabel(m)}</option>)}
          </select>
          {cityNames.length > 0 && (
            <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="whatson-city"
              className="bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-white outline-none focus:border-[#2ec9c9] transition-colors">
              <option value="all" className="text-[#16243b]">All cities</option>
              {cityNames.map((c) => <option key={c} value={c} className="text-[#16243b]">{c}</option>)}
            </select>
          )}
          {venueNames.length > 0 && (
            <select value={venue} onChange={(e) => setVenue(e.target.value)} data-testid="whatson-venue"
              className="bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-white outline-none focus:border-[#2ec9c9] transition-colors">
              <option value="all" className="text-[#16243b]">All venues</option>
              {venueNames.map((v) => <option key={v} value={v} className="text-[#16243b]">{v}</option>)}
            </select>
          )}
        </div>

        {nearName && city === nearName && (
          <p className="text-xs text-[#2ec9c9] mb-4 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="whatson-near-hint">
            <span className="flex items-center gap-1.5"><MapPin size={13} /> Showing events near you in {nearName}.</span>
            <Link to={`/city/${slugify(nearName)}`} className="underline hover:text-white transition-colors">View the {nearName} page</Link>
            <button onClick={() => setCity("all")} className="underline hover:text-white transition-colors">Show all cities</button>
          </p>
        )}

        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="whatson-categories">
            <button onClick={() => setCategory("all")} data-testid="category-all"
              className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${category === "all" ? "bg-[#2ec9c9] text-[#0d1626] border-[#2ec9c9] font-medium" : "text-[#8a97a8] border-white/10 hover:border-[#2ec9c9]/50"}`}>All</button>
            {categories.map((c) => {
              const cm = catMeta(c); const CIcon = cm.icon; const on = category === c;
              return (
              <button key={c} onClick={() => setCategory(c)} data-testid={`category-${c}`}
                className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors flex items-center gap-1.5 ${on ? "font-medium" : "hover:opacity-90"}`}
                style={on ? { background: cm.color, color: "#0d1626", borderColor: cm.color } : { color: cm.color, borderColor: cm.color + "40" }}>
                <CIcon size={13} /> {c}
              </button>
            );})}
          </div>
        )}

        {artists.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10 items-center" data-testid="whatson-artists">
            <span className="text-xs uppercase tracking-wide text-[#8a97a8] mr-1 flex items-center gap-1.5"><Mic2 size={13} /> Artists</span>
            <button onClick={() => setArtist("all")} data-testid="artist-all"
              className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${artist === "all" ? "bg-[#8b5cf6] text-white border-[#8b5cf6] font-medium" : "text-[#8a97a8] border-white/10 hover:border-[#8b5cf6]/60"}`}>All</button>
            {artists.map((a) => {
              const on = artist === a.slug;
              return (
                <button key={a.slug} onClick={() => setArtist(on ? "all" : a.slug)} data-testid={`artist-chip-${a.slug}`}
                  className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${on ? "bg-[#8b5cf6] text-white border-[#8b5cf6] font-medium" : "text-[#c4b5fd] border-[#8b5cf6]/40 hover:border-[#8b5cf6]"}`}>
                  {a.name}
                </button>
              );
            })}
          </div>
        )}

        {dates.length === 0 ? (
          <p className="text-[#8a97a8] py-10 text-center" data-testid="whatson-empty">No events match your search.</p>
        ) : (
        <div className="space-y-10">
          {dates.map((date) => (
            <div key={date} data-testid={`event-day-${date}`}>
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-2 text-white font-semibold">
                  <CalendarDays size={18} className="text-[#2ec9c9]" />
                  <span data-testid="event-day-label">{date === "tbc" ? "Date to be confirmed" : dayLabel(date)}</span>
                </div>
                <span className="text-xs text-[#8a97a8] bg-white/5 rounded-full px-2.5 py-1">{groups[date].length} event{groups[date].length !== 1 ? "s" : ""}</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {groups[date].map((e) => {
                  const cm = catMeta(e.category); const CIcon = cm.icon;
                  return (
                  <a key={e.id} href={`/store/${e.business_slug}/events/${e.slug}`} data-testid={`event-card-${e.id}`}
                    className="group flex gap-4 rounded-2xl bg-white/[0.04] border border-white/10 p-3 min-w-0 w-full hover:bg-white/[0.08] hover:border-[#2ec9c9]/40 transition-colors">
                    <div className="w-20 h-20 rounded-xl bg-white/10 overflow-hidden shrink-0">
                      {e.image_url && <img src={e.image_url} alt={e.title} className="w-full h-full object-cover" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-[#2ec9c9] flex items-center gap-1.5">{e.pinned && <Pin size={11} />}{timeLabel(e.event_date)}{e.pinned && <span className="text-[10px] uppercase tracking-wide text-[#2ec9c9]/80">Featured</span>}</p>
                      <h3 className="text-white font-semibold truncate group-hover:text-[#2ec9c9] transition-colors">{e.title}</h3>
                      <p className="text-xs text-[#8a97a8] mt-0.5 truncate">{e.business_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {e.category && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-1 shrink-0" style={{ background: cm.color + "26", color: cm.color }}><CIcon size={10} /> {e.category}</span>}
                        {e.venue_name && <span className="text-xs text-[#8a97a8] flex items-center gap-1 truncate"><MapPin size={11} /> {e.venue_name}</span>}
                      </div>
                    </div>
                    <div className="self-center text-right shrink-0">
                      <p className="text-white font-bold text-sm">£{e.from_price.toFixed(2)}</p>
                      <p className="text-[10px] text-[#8a97a8]">from</p>
                      <ArrowRight size={16} className="text-[#8a97a8] group-hover:text-[#2ec9c9] transition-colors ml-auto mt-1" />
                    </div>
                  </a>
                );})}
              </div>
            </div>
          ))}
        </div>
        )}

        <div className="mt-10 text-center">
          <Link to="/events" data-testid="browse-all-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[#2ec9c9] text-[#2ec9c9] hover:bg-[#2ec9c9] hover:text-[#0d1626] font-medium px-6 py-2.5 transition-colors">
            Browse all events <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
